import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Check, Mail, RefreshCw, ShieldCheck, Plug, Unplug, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CalendarsSection from "@/components/settings/CalendarsSection";

const STATUS_FR = { CONNECTED: "Connecté", NOT_CONNECTED: "Non connecté", ERROR: "Erreur", AUTH_REQUIRED: "Autorisation requise", SYNCING: "Synchronisation", PAUSED_RATE_LIMIT: "Ralenti par Google" };

export default function Settings() {
  const [rules, setRules] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();
  const connected = accounts.length > 0;

  const loadStatus = async () => {
    const s = await api.get("/gmail/status");
    setAccounts(s.data.accounts || []);
  };

  const [syncCfg, setSyncCfg] = useState({ interval_minutes: 10, enabled: true, last_run_at: null });
  const [sys, setSys] = useState(null);
  const loadSys = async () => { try { const r = await api.get("/system/status"); setSys(r.data); } catch { /* noop */ } };
  const saveSyncCfg = async (updates) => {
    setSyncCfg((c) => ({ ...c, ...updates }));
    const res = await api.put("/sync/config", updates);
    setSyncCfg(res.data);
  };
  const syncAll = async () => {
    setBusy("syncall");
    try {
      const res = await api.post("/sync/all");
      toast.success(`Synchro terminée — ${res.data.emails} email(s), ${res.data.events} événement(s), ${res.data.tasks} tâche(s)`);
      await loadStatus(); await loadSys();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync impossible"); } finally { setBusy(""); }
  };

  useEffect(() => {
    Promise.all([api.get("/rules"), api.get("/gmail/status"), api.get("/sync/config"), api.get("/system/status")]).then(([r, s, c, sy]) => {
      setRules(r.data); setAccounts(s.data.accounts || []); setSyncCfg(c.data); setSys(sy.data);
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const g = params.get("gmail");
    if (g === "connected") { toast.success("Compte Google connecté !"); loadStatus(); setParams({}); }
    else if (g === "error") { toast.error("Connexion Google échouée"); setParams({}); }
  }, [params, setParams]);

  const save = async () => {
    setSaving(true);
    try { await api.put("/rules", rules); toast.success("Règles enregistrées"); }
    catch { toast.error("Erreur"); } finally { setSaving(false); }
  };

  const connect = async () => {
    setBusy("connect");
    try { const res = await api.get("/gmail/oauth/start"); window.location.href = res.data.url; }
    catch { toast.error("Connexion indisponible"); setBusy(""); }
  };
  const patchAccount = async (id, updates) => {
    setAccounts((prev) => prev.map((a) => a.id === id ? { ...a, ...updates } : a));
    await api.patch(`/accounts/${id}`, updates);
  };
  const syncAccount = async (id) => {
    setBusy(`sync-${id}`);
    try {
      const res = await api.post(`/accounts/${id}/sync`);
      await loadStatus(); await loadSys();
      toast.success(res.data.imported > 0 ? `${res.data.imported} email(s) importé(s)` : "Synchro à jour");
    }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync impossible"); } finally { setBusy(""); }
  };
  const disconnect = async (id, del) => {
    if (del && !window.confirm("Supprimer aussi tous les emails/tâches importés de ce compte ?")) return;
    await api.delete(`/accounts/${id}`, { params: { delete_data: !!del } });
    await loadStatus();
    toast.success("Compte déconnecté");
  };

  if (loading || !rules) return <div className="h-[40vh] flex items-center justify-center text-muted-foreground"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const p = rules.personal || {};
  const setP = (k, v) => setRules({ ...rules, personal: { ...p, [k]: v } });

  const field = "bg-secondary border border-border rounded-xl px-3 py-2 text-sm";

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="font-display text-3xl font-semibold tracking-tight">Réglages</h1>
        <p className="text-muted-foreground mt-1 text-sm">Tes règles de travail et tes connexions. L'IA les respectera pour construire ton planning.</p>
      </div>

      {/* System Status (diagnostic) */}
      {sys && (
        <section className="rounded-2xl border border-border bg-card p-6" data-testid="settings-system-status">
          <h2 className="font-display text-lg font-semibold mb-4">État du système</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[["API Gmail", sys.gmail_api, "Gmail API"], ["API Calendrier", sys.calendar_api, "Calendar API"], ["Moteur IA", sys.ai_engine, "AI Engine"], ["Base de données", sys.database, "Database"]].map(([l, v, tid]) => (
              <div key={l} className="rounded-xl border border-border p-3" data-testid={`sysstat-${tid}`}>
                <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{l}</div>
                <div className={`text-sm font-semibold mt-1 ${v === "CONNECTED" ? "text-emerald-400" : v === "NOT_CONNECTED" ? "text-zinc-400" : "text-rose-400"}`}>{STATUS_FR[v] || v}</div>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 text-sm">
            <Stat label="Comptes Gmail" value={sys.connected_gmail_accounts} />
            <Stat label="Emails importés" value={sys.imported_emails} />
            <Stat label="Événements importés" value={sys.imported_calendar_events} />
            <Stat label="Tâches (depuis emails)" value={sys.generated_tasks_from_email} />
          </div>
          <div className="text-xs text-muted-foreground mt-3">
            Dernière synchro Gmail : {sys.last_gmail_sync ? new Date(sys.last_gmail_sync).toLocaleString("fr-FR") : "—"} · Dernière synchro Calendrier : {sys.last_calendar_sync ? new Date(sys.last_calendar_sync).toLocaleString("fr-FR") : "—"}
          </div>
        </section>
      )}

      <CalendarsSection />

      {/* Connections */}
      <section className="rounded-2xl border border-border bg-card p-6" data-testid="settings-connections">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Plug className="w-4 h-4 text-primary" /><h2 className="font-display text-lg font-semibold">Comptes Google connectés</h2></div>
          <button data-testid="settings-connect-google" onClick={connect} disabled={busy === "connect"}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm text-primary-foreground font-medium hover:opacity-90">
            {busy === "connect" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Ajouter un compte
          </button>
        </div>

        {!connected ? (
          <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
            Aucun compte connecté. Connecte Gmail pour importer tes vrais emails et événements.<br />
            Clique « Ajouter un compte » et connecte-toi avec chaque adresse (le sélecteur de compte Google s'affiche à chaque fois). Aucune donnée fictive n'est affichée.
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((a) => {
              const isDemo = a.provider === "demo";
              const st = a.sync_status;
              const stCls = st === "CONNECTED" ? "text-emerald-400 bg-emerald-500/10"
                : st === "SYNCING" ? "text-sky-400 bg-sky-500/10"
                : st === "AUTH_REQUIRED" ? "text-amber-400 bg-amber-500/10"
                : st === "PAUSED_RATE_LIMIT" ? "text-amber-400 bg-amber-500/10"
                : st === "ERROR" ? "text-rose-400 bg-rose-500/10" : "text-zinc-400 bg-zinc-500/10";
              const stLabel = { CONNECTED: "Connecté", SYNCING: "Synchro…", AUTH_REQUIRED: "Reconnexion requise",
                ERROR: "Erreur", PAUSED_RATE_LIMIT: "Ralenti par Google", NOT_CONNECTED: "Non connecté" }[st] || st;
              const errCls = st === "PAUSED_RATE_LIMIT" || st === "AUTH_REQUIRED" ? "text-amber-400" : "text-rose-400";
              const ss = a.sync_stats || {};
              return (
                <div key={a.id} data-testid={`account-${a.email_address}`} className="rounded-xl border border-border p-4">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><Mail className="w-4 h-4" /></div>
                      <div className="min-w-0">
                        <input data-testid={`account-name-${a.email_address}`} value={a.display_name || ""}
                          onChange={(e) => setAccounts((p) => p.map((x) => x.id === a.id ? { ...x, display_name: e.target.value } : x))}
                          onBlur={(e) => patchAccount(a.id, { display_name: e.target.value })}
                          className="bg-transparent text-sm font-medium outline-none border-b border-transparent focus:border-border w-full" />
                        <div className="text-xs text-muted-foreground truncate">{a.email_address}{a.last_sync_at ? ` · sync ${new Date(a.last_sync_at).toLocaleTimeString("fr-FR",{hour:'2-digit',minute:'2-digit'})}` : " · jamais synchronisé"}{typeof a.messages_imported === "number" ? ` · ${a.messages_imported} msg importés` : ""}</div>
                        {(ss.discovered != null || ss.imported != null) && (
                          <div className="text-[11px] text-muted-foreground mt-0.5 font-mono" data-testid={`account-stats-${a.email_address}`}>
                            découverts {ss.discovered ?? 0} · importés {ss.imported ?? 0} · restants {ss.remaining ?? 0} · appels API {ss.api_calls_last_run ?? 0} · rate-limits {ss.rate_limit_events ?? 0}
                          </div>
                        )}
                        {st === "PAUSED_RATE_LIMIT" && (
                          <div className="text-xs text-amber-400 mt-0.5" data-testid={`account-paused-${a.email_address}`}>
                            Google limite temporairement la vitesse de synchronisation. AIRTIME reprendra automatiquement.
                          </div>
                        )}
                        {a.last_error && st !== "PAUSED_RATE_LIMIT" && <div className={`text-xs ${errCls} mt-0.5`} data-testid={`account-error-${a.email_address}`}>⚠ {a.last_error}</div>}
                      </div>
                    </div>
                    <span className={`font-mono text-[10px] px-2 py-1 rounded-full ${stCls}`} data-testid={`account-status-${a.email_address}`}>{stLabel}</span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                    <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Type</span>
                      <select data-testid={`account-type-${a.email_address}`} value={a.account_type} onChange={(e) => patchAccount(a.id, { account_type: e.target.value })} className={field}>
                        <option value="WORK">Pro</option><option value="WORK_LEGACY">Pro (ancien)</option><option value="PERSONAL">Perso</option><option value="OTHER">Autre</option>
                      </select>
                    </label>
                    <label className="flex flex-col gap-1"><span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Priorité</span>
                      <select data-testid={`account-priority-${a.email_address}`} value={a.priority_level} onChange={(e) => patchAccount(a.id, { priority_level: e.target.value })} className={field}>
                        <option value="HIGH">Haute</option><option value="NORMAL">Normale</option><option value="LOW">Basse</option>
                      </select>
                    </label>
                    <Toggle label="Détection tâches" checked={a.task_detection_enabled} onChange={(v) => patchAccount(a.id, { task_detection_enabled: v })} testid={`account-taskdet-${a.email_address}`} />
                    <Toggle label="Daily Brief" checked={a.daily_brief_enabled} onChange={(v) => patchAccount(a.id, { daily_brief_enabled: v })} testid={`account-brief-${a.email_address}`} />
                  </div>

                  <div className="flex flex-wrap gap-2 mt-4">
                    {!isDemo && <button data-testid={`account-sync-${a.email_address}`} onClick={() => syncAccount(a.id)} disabled={busy === `sync-${a.id}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-accent">
                      {busy === `sync-${a.id}` ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Synchroniser
                    </button>}
                    {!isDemo && <button data-testid={`account-reconnect-${a.email_address}`} onClick={connect}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs hover:bg-accent">
                      <Plug className="w-3.5 h-3.5" /> Reconnecter
                    </button>}
                    <button data-testid={`account-disconnect-${a.email_address}`} onClick={() => disconnect(a.id, false)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-rose-400 hover:bg-rose-500/10">
                      <Unplug className="w-3.5 h-3.5" /> Déconnecter
                    </button>
                    <button data-testid={`account-delete-${a.email_address}`} onClick={() => disconnect(a.id, true)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1.5 text-xs text-rose-400 hover:bg-rose-500/10">
                      <X className="w-3.5 h-3.5" /> Supprimer les données
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Auto-sync */}
      <section className="rounded-2xl border border-border bg-card p-6" data-testid="settings-autosync">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2"><RefreshCw className="w-4 h-4 text-primary" /><h2 className="font-display text-lg font-semibold">Synchronisation automatique</h2></div>
          <button data-testid="settings-sync-all" onClick={syncAll} disabled={busy === "syncall" || !connected}
            className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-2 text-sm font-medium hover:bg-accent transition-all disabled:opacity-50">
            {busy === "syncall" ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Tout synchroniser maintenant
          </button>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          AIRTIME synchronise tous tes comptes activés en arrière-plan, sans clic manuel.
          {syncCfg.last_run_at && ` Dernière passe : ${new Date(syncCfg.last_run_at).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}.`}
        </p>
        <div className="flex flex-wrap items-end gap-4 mt-4">
          <Toggle label="Activée" checked={syncCfg.enabled} onChange={(v) => saveSyncCfg({ enabled: v })} testid="autosync-toggle" />
          <label className="flex flex-col gap-1">
            <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">Intervalle (minutes)</span>
            <input data-testid="autosync-interval" type="number" min="1" max="120" value={syncCfg.interval_minutes}
              onChange={(e) => setSyncCfg((c) => ({ ...c, interval_minutes: e.target.value }))}
              onBlur={(e) => saveSyncCfg({ interval_minutes: Number(e.target.value) || 10 })}
              className={field + " w-28"} />
          </label>
        </div>
      </section>
      <section className="rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="settings-rules">
        <h2 className="font-display text-lg font-semibold">Règles de travail</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Labeled label="Début de journée"><input data-testid="rules-start" type="time" className={field} value={rules.working_hours_start} onChange={(e) => setRules({ ...rules, working_hours_start: e.target.value })} /></Labeled>
          <Labeled label="Fin de journée"><input data-testid="rules-end" type="time" className={field} value={rules.working_hours_end} onChange={(e) => setRules({ ...rules, working_hours_end: e.target.value })} /></Labeled>
          <Labeled label="Max réunions / jour"><input type="number" className={field} value={rules.max_meetings_per_day} onChange={(e) => setRules({ ...rules, max_meetings_per_day: Number(e.target.value) })} /></Labeled>
          <Labeled label="Déjeuner début"><input type="time" className={field} value={rules.lunch_start} onChange={(e) => setRules({ ...rules, lunch_start: e.target.value })} /></Labeled>
          <Labeled label="Déjeuner fin"><input type="time" className={field} value={rules.lunch_end} onChange={(e) => setRules({ ...rules, lunch_end: e.target.value })} /></Labeled>
          <Labeled label="Deep work min (min)"><input type="number" className={field} value={rules.min_deep_work_minutes} onChange={(e) => setRules({ ...rules, min_deep_work_minutes: Number(e.target.value) })} /></Labeled>
          <Labeled label="Travail max / jour (min)"><input type="number" className={field} value={rules.max_work_minutes} onChange={(e) => setRules({ ...rules, max_work_minutes: Number(e.target.value) })} /></Labeled>
          <Labeled label="Pas de réunion"><input className={field} value={rules.no_meetings || ""} onChange={(e) => setRules({ ...rules, no_meetings: e.target.value })} /></Labeled>
        </div>
      </section>

      {/* Golf / personal */}
      <section className="rounded-2xl border border-border bg-card p-6 space-y-5" data-testid="settings-golf">
        <h2 className="font-display text-lg font-semibold">Temps personnel · Golf</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Labeled label="Durée idéale (min, transport inclus)"><input data-testid="golf-ideal" type="number" className={field} value={p.golf_ideal_minutes || 330} onChange={(e) => setP("golf_ideal_minutes", Number(e.target.value))} /></Labeled>
          <Labeled label="Transport A/R (min)"><input type="number" className={field} value={p.golf_transport_minutes || 60} onChange={(e) => setP("golf_transport_minutes", Number(e.target.value))} /></Labeled>
          <Labeled label="Départ au plus tard"><input type="time" className={field} value={p.golf_latest_start || "13:00"} onChange={(e) => setP("golf_latest_start", e.target.value)} /></Labeled>
        </div>
        <div>
          <div className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-2">Jours préférés</div>
          <div className="flex flex-wrap gap-2">
            {["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"].map((d) => {
              const arr = p.preferred_golf_days || [];
              const on = arr.includes(d);
              return (
                <button key={d} data-testid={`golf-day-${d}`} onClick={() => setP("preferred_golf_days", on ? arr.filter((x) => x !== d) : [...arr, d])}
                  className={`rounded-full px-3 py-1.5 text-xs border transition-all ${on ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-border text-muted-foreground hover:text-foreground"}`}>
                  {d}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <button data-testid="settings-save" onClick={save} disabled={saving}
        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm text-primary-foreground font-medium hover:opacity-90 transition-all disabled:opacity-60">
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Enregistrer
      </button>

      {/* Privacy */}
      <section className="rounded-2xl border border-border bg-card/50 p-6" data-testid="settings-privacy">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-4 h-4 text-emerald-400" /><h2 className="font-display text-base font-semibold">Confidentialité</h2></div>
        <ul className="text-sm text-muted-foreground space-y-1.5">
          <li>· Connexion via OAuth — aucun mot de passe email n'est stocké.</li>
          <li>· Tes tokens Google sont chiffrés au repos (Fernet).</li>
          <li>· L'analyse IA envoie le contenu des emails à un LLM uniquement pour extraire tâches et résumés.</li>
          <li>· Aucun email n'est jamais envoyé automatiquement sans ta validation.</li>
        </ul>
      </section>
    </div>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="font-display text-2xl font-semibold">{value}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Toggle({ label, checked, onChange, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <button data-testid={testid} onClick={() => onChange(!checked)} type="button"
        className={`h-8 rounded-lg border text-xs font-medium transition-all ${checked ? "bg-emerald-500/15 border-emerald-500/40 text-emerald-300" : "border-border text-muted-foreground"}`}>
        {checked ? "Activé" : "Désactivé"}
      </button>
    </label>
  );
}
